// TGCLOUD-1818 — display strings for the filter chips.
//
// DISPLAY ONLY. The field and operator allowlist comes from the server
// (`GET /users/proband/filter-fields`); nothing here decides what is filterable.
// If this file and the server disagree about which fields exist, the server
// wins and the extra entry here is simply never reached.

import {
  OPERATORS,
  splitTerms,
  isValueless,
  isRange
} from './filterPredicates';

/**
 * i18next keys for each operator, as they read INSIDE a chip.
 *
 * Phrased to complete the sentence "<Field> <operator> <value>", which is why
 * they are lower-case and why `isOneOf` is "is one of" rather than "In". A chip
 * has to read as a sentence or the operator becomes decoration.
 */
export const OPERATOR_LABEL_KEYS = Object.freeze({
  [OPERATORS.IS]: 'is',
  [OPERATORS.IS_ONE_OF]: 'is one of',
  [OPERATORS.CONTAINS]: 'contains',
  [OPERATORS.STARTS_WITH]: 'starts with',
  [OPERATORS.IN_YEAR]: 'in year',
  [OPERATORS.BEFORE]: 'before',
  [OPERATORS.AFTER]: 'after',
  [OPERATORS.BETWEEN]: 'between',
  [OPERATORS.IS_EMPTY]: 'is empty',
  [OPERATORS.IS_NOT_EMPTY]: 'is not empty'
});

/** Longest value rendered inside a chip before it is elided. */
export const VALUE_TRUNCATE_AT = 20;

/**
 * Resolve a field's display label.
 *
 * Order matters. A tenant that has renamed "Hospital Number" to "MRN" must see
 * MRN on the chip, so the relabellable map wins; otherwise the field's own
 * i18next key is used, and that key is deliberately the SAME string the grid
 * column header uses, so a chip and its column can never drift apart.
 *
 * @param {{key:string, labelKey:string, relabellable?:boolean}} field
 * @param {(s: string) => string} t
 * @param {Record<string,string>} [tenantLabels] from usePedigreeFieldLabels
 * @returns {string}
 */
export const fieldLabel = (field, t, tenantLabels) => {
  if (!field) return '';
  if (field.relabellable && tenantLabels?.[field.key]) {
    return tenantLabels[field.key];
  }
  return t(field.labelKey || field.key);
};

/**
 * Render a criterion's value for display.
 *
 * Enum values are shown through their own display map (a relationship is stored
 * as a code and shown as "Mother's Mother"), multi-value operators are joined
 * with a comma, and a range reads "A – B".
 *
 * @param {{operator:string, value:*}} criterion
 * @param {{type:string}} field
 * @param {(s:string)=>string} t
 * @param {Record<string,string>} [valueLabels] code -> display, for enums
 * @returns {string}
 */
export const valueText = (criterion, field, t, valueLabels) => {
  if (!criterion || isValueless(criterion.operator)) return '';
  const { operator, value } = criterion;

  if (isRange(operator)) {
    const from = value?.from || '';
    const to = value?.to || '';
    if (!from && !to) return '';
    return `${from} – ${to}`;
  }

  if (operator === OPERATORS.IS_ONE_OF) {
    const terms = splitTerms(value);
    const shown = valueLabels
      ? terms.map((term) => valueLabels[term] ?? term)
      : terms;
    return shown.join(', ');
  }

  const raw = String(value ?? '');
  if (!raw) return '';
  if (valueLabels && field?.type === 'enum') return valueLabels[raw] ?? raw;
  return raw;
};

/**
 * Truncate a chip value, keeping the full text for the `title` attribute.
 *
 * @param {string} text
 * @param {number} [limit]
 * @returns {{shown: string, truncated: boolean}}
 */
export const truncateValue = (text, limit = VALUE_TRUNCATE_AT) => {
  const str = String(text ?? '');
  if (str.length <= limit) return { shown: str, truncated: false };
  return { shown: `${str.slice(0, limit)}…`, truncated: true };
};

/**
 * The full sentence a chip represents, unabbreviated.
 *
 * This is what the remove button announces to a screen reader, so it must name
 * the field, the operator AND the value — "Remove" alone tells a non-sighted
 * user nothing about which of five chips they are about to delete.
 *
 * @returns {string} e.g. "Family ID is G-0-01520"
 */
export const criterionSentence = (criterion, field, t, opts = {}) => {
  const label = fieldLabel(field, t, opts.tenantLabels);
  const operator = t(OPERATOR_LABEL_KEYS[criterion?.operator] || '');
  const value = valueText(criterion, field, t, opts.valueLabels);
  return [label, operator, value].filter(Boolean).join(' ');
};
